import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type { AutomationRuleSummary } from '../__types__/AutomationRuleSummaryQuery.ts';
import { ButtonState } from '../buttonStates.ts';

import { RulePreview } from './rule-preview/RulePreview.tsx';

export const i18n = defineMessages({
	removedTooltip: {
		id: 'automation-extensions.smart-button.tooltip.removed',
		defaultMessage: 'Smart button has been removed. Contact your admin.',
		description: 'Message on the smart button hover tooltip if the button rule was removed',
	},
	disabledTooltip: {
		id: 'automation-extensions.smart-button.tooltip.disabled',
		defaultMessage: 'Smart button has been disabled. Contact your admin.',
		description: 'Message on the smart button hover tooltip if the button rule was disabled',
	},
	movedTooltip: {
		id: 'automation-extensions.smart-button.tooltip.moved',
		defaultMessage: 'This smart button runs a rule in another space. Contact your admin.',
		description:
			'Message on the smart button hover tooltip if the button rule was moved to another space',
	},
	premiumOnlyTooltip: {
		id: 'automation-extensions.smart-button.tooltip.premium-only',
		defaultMessage: 'Smart buttons are a Premium feature. Contact your admin.',
		description:
			'Message on the smart button hover tooltip if the instance edition is not high enough to use automation',
	},
	pageOnlyTooltip: {
		id: 'automation-extensions.smart-button.tooltip.page-only',
		defaultMessage: 'Smart buttons don’t work on blogs. We’ve disabled this button.',
		description: 'Message on the smart button hover tooltip if the current content is a blogpost',
	},
	unconfiguredTooltip: {
		id: 'automation-extensions.smart-button.tooltip.unconfigured',
		defaultMessage:
			'This button doesn’t have a rule configured. To create your own button, select the (+) icon from the toolbar or type /smart button.',
		description:
			'Message on the smart button hover tooltip if the smart button is currently unconfigured',
	},
	missingPermissionsWithOwnerTooltip: {
		id: 'automation-extensions.smart-button.tooltip.missing-permissions-with-owner',
		defaultMessage: '{userName} owns this rule and is the only one who can edit it.',
		description:
			'Message on the smart button hover tooltip if the user does not have sufficient permissions to view the smart button with owner name',
	},
	missingPermissionsDefaultTooltip: {
		id: 'automation-extensions.smart-button.tooltip.missing-permissions-default',
		defaultMessage: 'This rule’s owner is the only person who can edit it.',
		description:
			'Message on the smart button hover tooltip if the user does not have sufficient permissions to view the smart button and we do not have the owner info',
	},
	AIDisabledAndAIRuleTooltip: {
		id: 'automation-extensions.smart-button.tooltip.ai-disabled-and-ai-rule-button',
		defaultMessage:
			'Smart button is disabled because your admin has turned off Atlassian Intelligence.',
		description:
			'Message on the smart button hover tooltip if Atlassian Intelligence is disabled for the instance and the button contains a rule with AI.',
	},
	predefinedTemplateTooltip: {
		id: 'automation-extensions.smart-button.tooltip.predefined-template.unconfigured',
		defaultMessage: 'Button is not set up',
		description:
			'Message on the smart button hover tooltip if the smart button is currently unconfigured with a predefined template, while in edit mode.',
	},
	editModeUnconfiguredTooltip: {
		id: 'automation-extensions.smart-button.tooltip.edit-mode.unconfigured',
		defaultMessage: 'Button has no actions',
		description:
			'Message on the smart button hover tooltip if the smart button is currently unconfigured while in edit mode.',
	},
	ruleNotRunnableTooltip: {
		id: 'automation-extensions.smart-button.tooltip.rule-not-runnable',
		defaultMessage:
			'There was a problem and we couldn’t run the automation. Refresh the page. Contact support if this persists.',
		description:
			'Message on the smart button hover tooltip if the smart button rule is not runnable.',
	},
	clickToEdit: {
		id: 'automation-extensions.smart-button.tooltip.click-to-edit',
		defaultMessage: 'Click to edit',
		description:
			'Message on the smart button hover tooltip to click the button to begin the configuration process.',
	},
	selectToEdit: {
		id: 'automation-extensions.smart-button.tooltip.select-to-edit',
		defaultMessage: 'Select to edit',
		description:
			'Message on the smart button hover tooltip to select the button to begin the configuration process.',
	},
});

export type SmartButtonTooltipContentProps = {
	buttonState: ButtonState;
	userName?: string | null;
	automationRuleSummaryData?: AutomationRuleSummary;
	automationRuleSummaryLoading: boolean;
	automationRuleSummaryError?: ApolloErrorInterop | Error;
};

export const getSmartButtonTooltipContent = ({
	buttonState,
	userName,
	automationRuleSummaryData,
	automationRuleSummaryLoading,
	automationRuleSummaryError,
}: SmartButtonTooltipContentProps) => {
	switch (buttonState) {
		case ButtonState.RULE_DELETED:
			return () => <FormattedMessage {...i18n.removedTooltip} />;
		case ButtonState.RULE_DISABLED:
			return () => <FormattedMessage {...i18n.disabledTooltip} />;
		case ButtonState.RULE_MOVED:
			return () => <FormattedMessage {...i18n.movedTooltip} />;
		case ButtonState.NOT_AVAILABLE:
			return () => <FormattedMessage {...i18n.premiumOnlyTooltip} />;
		case ButtonState.INVALID_CONTENT_TYPE:
			return () => <FormattedMessage {...i18n.pageOnlyTooltip} />;
		case ButtonState.VIEW_RULE_NOT_CONFIGURED:
			return () => (
				<Stack space="space.100">
					<Text size="medium">
						<FormattedMessage {...i18n.unconfiguredTooltip} />
					</Text>
				</Stack>
			);
		case ButtonState.EDIT_RULE_NOT_CONFIGURED:
			return () => (
				// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				<Inline separator={<>·</>} space="space.050">
					<FormattedMessage {...i18n.editModeUnconfiguredTooltip} />
					<FormattedMessage {...i18n.clickToEdit} />
				</Inline>
			);
		case ButtonState.PREDEFINED_TEMPLATE_NOT_CONFIGURED:
			return () => (
				// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				<Inline separator={<>·</>} space="space.050">
					<FormattedMessage {...i18n.predefinedTemplateTooltip} />
					<FormattedMessage {...i18n.selectToEdit} />
				</Inline>
			);
		case ButtonState.MISSING_PERMISSIONS:
			return () =>
				userName ? (
					<FormattedMessage {...i18n.missingPermissionsWithOwnerTooltip} values={{ userName }} />
				) : (
					<FormattedMessage {...i18n.missingPermissionsDefaultTooltip} />
				);
		case ButtonState.LOADING:
		case ButtonState.RULE_CONFIGURED:
			return () => (
				<RulePreview
					automationRuleSummaryData={automationRuleSummaryData}
					automationRuleSummaryLoading={automationRuleSummaryLoading}
					automationRuleSummaryError={automationRuleSummaryError}
				/>
			);
		case ButtonState.AI_DISABLED:
			return () => <FormattedMessage {...i18n.AIDisabledAndAIRuleTooltip} />;
		case ButtonState.RULE_NOT_RUNNABLE:
			return () => <FormattedMessage {...i18n.ruleNotRunnableTooltip} />;
		case ButtonState.EDIT_RULE_CONFIGURED:
			return;
		default:
			// If there is a TS error on this line, a ButtonState is not being handled in this switch statement.
			const exhaustiveCheck: never = buttonState;
			throw new Error(`Unhandled case: ${exhaustiveCheck}`);
	}
};
